import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { ISearchParameters } from '../../../contexts/Search/types';
import { CalendarIcon } from '../../../images/icons/calendar';
import { ChevronIcon } from '../../../images/icons/chevron';
import { CloseIcon } from '../../../images/icons/close';
import { PinIcon } from '../../../images/icons/pin';
import { SwitchIcon } from '../../../images/icons/switch';
import { UserIcon } from '../../../images/icons/user';
import { UsersIcon } from '../../../images/icons/users';
import { dayjs } from '../../../libs/day';
import { colors, screens } from '../../../styles/constants';
import Button from '../../Forms/Button';
import IconTextField from '../../Forms/Input/IconTextField';
import { H4 } from '../../Typographies/Title';
import {
  TSB_DEPARTURE_CITY_INPUT_TEST_ID,
  TSB_DEPARTURE_DATE_INPUT_TEST_ID,
  TSB_DESTINATION_CITY_INPUT_TEST_ID,
  TSB_PASSENGERS_INPUT_TEST_ID,
  TSB_REMOVE_RETURN_DATE_BUTTON_TEST_ID,
  TSB_RETURN_DATE_INPUT_TEST_ID,
  TSB_SUBMIT_BUTTON_TEST_ID,
  TSB_SWITCH_CITIES_BUTTON_TEST_ID,
} from '../constants';
import messages from '../messages';
import { ESearchPanel } from '../SearchPanel';
import { PassengersRecap } from '../SearchPanel/PassengersPanel';

const Container = styled.div`
  max-width: 444px;
  height: max-content;
  ${tw`w-full bg-white rounded-30 flex flex-col px-medium2 pt-medium pb-big  shadow-medium 
    md:p-big   md:shadow-light
    `}

  gap:var(--size-medium);

  @media (min-width: ${screens.lg}) {
    gap: var(--size-big);
  }
`;

const Form = styled.form`
  ${tw`flex flex-col`}
  gap:var(--size-medium)
`;

const InputsWrapper = styled.fieldset`
  ${tw`relative p-0 m-0 border-0 flex flex-col`}

  button::first-letter {
    text-transform: uppercase;
  }
`;

const SubmitButtonStyle = css`
  ${tw`mt-small
    md:mt-0 md:mx-auto
  `}
`;

const Title = styled(H4)`
  ${tw`text-center`}
`;

const IconColor = colors['secondary-400'];

const SwitchButton = css`
  ${tw`absolute p-0 bg-primary-400 rounded-full flex justify-center items-center z-1 hover:bg-primary-400`}
  width:36px;
  height: 36px;
  right: 16px;
  top: 50%;

  transform: translateY(-50%);
  transition: transform 0.3s ease-in-out;

  &.rotated {
    transform: translateY(-50%) rotate(180deg);
  }

  &.reverse-rotation {
    transform: translateY(-50%) rotate(-180deg);
  }
`;

const ChevronStyle = css`
  transform: rotate(180deg);
`;

interface SearchBarProps {
  onOriginCityFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  onDestinationCityFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  onDepartureDateFocus: () => void;
  onReturnDateFocus: () => void;
  onPassengersFocus: () => void;
  onOriginCityChange: (city: string) => void;
  onDestinationCityChange: (city: string) => void;
  onOriginCityBlur: () => void;
  onDestinationCityBlur: () => void;

  onRemoveReturnDate: () => void;

  onReverseCities: () => void;

  searchParameters: ISearchParameters;

  formRef: React.MutableRefObject<HTMLFormElement>;
  originCityRef: React.MutableRefObject<HTMLInputElement>;
  destinationCityRef: React.MutableRefObject<HTMLInputElement>;
  departureDateRef: React.MutableRefObject<HTMLInputElement>;
  returnDateRef: React.MutableRefObject<HTMLInputElement>;
  passengersRef: React.MutableRefObject<HTMLInputElement>;
  onSubmit: () => void;
  displayedPanel: ESearchPanel;
  onOriginCityKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onDestinationCityKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  departureDateRef,
  destinationCityRef,
  displayedPanel,
  formRef,
  onDepartureDateFocus,
  onDestinationCityBlur,
  onDestinationCityChange,
  onDestinationCityFocus,
  onDestinationCityKeyDown,
  onOriginCityBlur,
  onOriginCityChange,
  onOriginCityFocus,
  onOriginCityKeyDown,
  onPassengersFocus,
  onRemoveReturnDate,
  onReturnDateFocus,
  onReverseCities,
  onSubmit,
  originCityRef,
  passengersRef,
  returnDateRef,
  searchParameters,
}) => {
  const switchButtonRef = useRef<HTMLButtonElement>(null);
  const discountCardsCount = searchParameters.passengers.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.discountCards.length,
    0,
  );

  return (
    <Container>
      <Title as="span">
        <FormattedMessage {...messages.formTitle} />
      </Title>
      <Form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <InputsWrapper>
          <FormattedMessage {...messages.departureCity}>
            {(msg) => (
              <IconTextField
                data-testid={TSB_DEPARTURE_CITY_INPUT_TEST_ID}
                displayOutline={displayedPanel === ESearchPanel.ORIGIN_CITY}
                inputRef={originCityRef}
                leftIcon={<PinIcon color={IconColor} />}
                onBlur={onOriginCityBlur}
                onChange={(e) => onOriginCityChange(e.target.value)}
                onFocus={onOriginCityFocus}
                onKeyDown={onOriginCityKeyDown}
                placeholder={msg}
                required
                rounded="TOP"
                type="text"
                value={searchParameters.origin}
              />
            )}
          </FormattedMessage>

          <FormattedMessage {...messages.arrivalCity}>
            {(msg) => (
              <IconTextField
                data-testid={TSB_DESTINATION_CITY_INPUT_TEST_ID}
                displayOutline={
                  displayedPanel === ESearchPanel.DESTINATION_CITY
                }
                inputRef={destinationCityRef}
                leftIcon={<PinIcon color={IconColor} />}
                onBlur={onDestinationCityBlur}
                onChange={(e) => onDestinationCityChange(e.target.value)}
                onFocus={onDestinationCityFocus}
                onKeyDown={onDestinationCityKeyDown}
                placeholder={msg}
                required
                rounded="BOTTOM"
                type="text"
                value={searchParameters.destination}
              />
            )}
          </FormattedMessage>

          <Button
            buttonRef={switchButtonRef}
            css={SwitchButton}
            data-testid={TSB_SWITCH_CITIES_BUTTON_TEST_ID}
            id="switch-od"
            onClick={() => {
              switchButtonRef.current.classList.toggle('rotated');
              onReverseCities();
            }}
            type="button"
            variant="PRIMARY"
          >
            <SwitchIcon />
          </Button>
        </InputsWrapper>

        <InputsWrapper>
          <IconTextField
            as="button"
            data-testid={TSB_DEPARTURE_DATE_INPUT_TEST_ID}
            displayOutline={displayedPanel === ESearchPanel.DEPARTURE_DATE}
            inputRef={departureDateRef}
            isFilled={true}
            leftIcon={
              <CalendarIcon
                color={IconColor}
                customStyle={css`
                  transform: rotateY(180deg);
                `}
              />
            }
            onClick={onDepartureDateFocus}
            onFocus={(event) => event.target.click()}
            rounded="TOP"
            type="button"
          >
            {dayjs(searchParameters.outboundDate).format('ddd D MMM')}
            {searchParameters.outboundTime > 0 &&
              ` · ${searchParameters.outboundTime}h`}
          </IconTextField>

          <IconTextField
            as="button"
            data-testid={TSB_RETURN_DATE_INPUT_TEST_ID}
            displayOutline={displayedPanel === ESearchPanel.RETURN_DATE}
            inputRef={returnDateRef}
            isFilled={!!searchParameters.returnDate}
            leftIcon={<CalendarIcon color={IconColor} />}
            onClick={onReturnDateFocus}
            onFocus={(event) => event.target.click()}
            rightIcon={
              searchParameters.returnDate && (
                <button
                  data-testid={TSB_REMOVE_RETURN_DATE_BUTTON_TEST_ID}
                  onClick={(e) => {
                    e.preventDefault();
                    onRemoveReturnDate();
                  }}
                  type="button"
                >
                  <CloseIcon color={colors['primary-400']} />
                </button>
              )
            }
            rounded="BOTTOM"
            type="button"
          >
            {searchParameters.returnDate ? (
              <React.Fragment>
                {dayjs(searchParameters.returnDate).format('ddd D MMM')}
                {searchParameters.returnTime > 0 &&
                  ` · ${searchParameters.returnTime}h`}
              </React.Fragment>
            ) : (
              <FormattedMessage {...messages.addReturn} />
            )}
          </IconTextField>
        </InputsWrapper>

        <IconTextField
          as="button"
          data-testid={TSB_PASSENGERS_INPUT_TEST_ID}
          displayOutline={displayedPanel === ESearchPanel.PASSENGERS}
          inputRef={passengersRef}
          isFilled={true}
          leftIcon={
            searchParameters.passengers.length > 1 ? (
              <UsersIcon color={IconColor} />
            ) : (
              <UserIcon color={IconColor} />
            )
          }
          onClick={onPassengersFocus}
          onFocus={(event) => event.target.click()}
          rounded="FULL"
          type="button"
        >
          {searchParameters.passengers.length === 1 ? (
            <PassengersRecap
              passengers={searchParameters.passengers}
              withAgeRange
            />
          ) : (
            <FormattedMessage
              {...messages.passengersCount}
              values={{ count: searchParameters.passengers.length }}
            />
          )}

          {discountCardsCount > 0 && (
            <React.Fragment>
              ,{' '}
              <FormattedMessage
                {...messages.cardsCount}
                values={{ count: discountCardsCount }}
              />
            </React.Fragment>
          )}
        </IconTextField>

        <Button
          css={SubmitButtonStyle}
          data-testid={TSB_SUBMIT_BUTTON_TEST_ID}
          rightIcon={<ChevronIcon customStyle={ChevronStyle} />}
          type="submit"
          variant="PRIMARY"
        >
          <FormattedMessage {...messages.search} />
        </Button>
      </Form>
    </Container>
  );
};

export default SearchBar;
